function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
import PropTypes from "prop-types";
function _objectWithoutProperties(e, t) {
  if (null == e) return {};
  var o,
    r,
    i = _objectWithoutPropertiesLoose(e, t);
  if (Object.getOwnPropertySymbols) {
    var n = Object.getOwnPropertySymbols(e);
    for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
  }
  return i;
}
function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (-1 !== e.indexOf(n)) continue;
    t[n] = r[n];
  }
  return t;
}
/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import classNames from 'classnames';
import { OuiStep } from './step';
function renderSteps(steps, firstStepNumber, headingElement, titleSize) {
  return steps.map(function (step, index) {
    var className = step.className,
      children = step.children,
      title = step.title,
      status = step.status,
      rest = _objectWithoutProperties(step, ["className", "children", "title", "status"]);
    return /*#__PURE__*/React.createElement(OuiStep, _extends({
      className: className,
      key: index,
      headingElement: headingElement,
      step: firstStepNumber + index,
      title: title,
      titleSize: titleSize,
      status: status
    }, rest), children);
  });
}
export var OuiSteps = ({
  className,
  firstStepNumber = 1,
  headingElement = 'p',
  titleSize,
  steps,
  ...rest
}) => {
  const classes = classNames('ouiSteps', className);
  return <div className={classes} {...rest}>
      {renderSteps(steps, firstStepNumber, headingElement, titleSize)}
    </div>;
};
OuiSteps.propTypes = {
  /**
     * An array of `OuiStep` objects excluding the `step` prop
     */
  steps: PropTypes.arrayOf(PropTypes.any.isRequired).isRequired,
  /**
     * The number the steps should begin from
     */
  firstStepNumber: PropTypes.number,
  /**
     * The HTML tag used for the title
     */
  headingElement: PropTypes.string,
  /**
     * Title sizing equivalent to OuiTitle, but only `m`, `s` and `xs`. Defaults to `s`
     */
  titleSize: PropTypes.any,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};
try {
  OuiSteps.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src/components/steps/steps.tsx',
    description: '',
    displayName: 'OuiSteps',
    methods: [],
    props: {
      steps: {
        defaultValue: null,
        description: 'An array of `OuiStep` objects excluding the `step` prop',
        name: 'steps',
        parent: {
          fileName: 'src/src/components/steps/steps.tsx',
          name: 'OuiStepsProps'
        },
        declarations: [{
          fileName: 'src/src/components/steps/steps.tsx',
          name: 'OuiStepsProps'
        }],
        required: true,
        type: {
          name: 'OuiContainedStepProps[]'
        }
      },
      firstStepNumber: {
        defaultValue: {
          value: '1'
        },
        description: 'The number the steps should begin from',
        name: 'firstStepNumber',
        parent: {
          fileName: 'src/src/components/steps/steps.tsx',
          name: 'OuiStepsProps'
        },
        declarations: [{
          fileName: 'src/src/components/steps/steps.tsx',
          name: 'OuiStepsProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      headingElement: {
        defaultValue: {
          value: 'p'
        },
        description: 'The HTML tag used for the title',
        name: 'headingElement',
        parent: {
          fileName: 'src/src/components/steps/steps.tsx',
          name: 'OuiStepsProps'
        },
        declarations: [{
          fileName: 'src/src/components/steps/steps.tsx',
          name: 'OuiStepsProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      titleSize: {
        defaultValue: null,
        description: 'Title sizing equivalent to OuiTitle, but only `m`, `s` and `xs`. Defaults to `s`',
        name: 'titleSize',
        parent: {
          fileName: 'src/src/components/steps/steps.tsx',
          name: 'OuiStepsProps'
        },
        declarations: [{
          fileName: 'src/src/components/steps/steps.tsx',
          name: 'OuiStepsProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"xs" | "s" | "m"',
          value: [{
            value: '"xs"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['OuiStepsProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}